import { Section } from "../components/LegalSection";

interface AccessibilityProps {
  onBack?: () => void;
}

export function Accessibility({ onBack }: AccessibilityProps) {
  return (
    <div className="px-5 pt-5 pb-10 flex flex-col gap-6 text-white">
      {/* Header */}
      <div>
        {onBack && (
          <button
            onClick={onBack}
            className="mb-4 flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            <span className="text-lg leading-none">&#8592;</span> Back
          </button>
        )}
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-purple-400 mb-1">
          Accessibility
        </p>
        <h1 className="text-2xl font-bold text-white">Accessibility Statement</h1>
        <p className="text-xs text-zinc-500 mt-1">Last updated: March 30, 2026</p>
      </div>

      {/* Commitment */}
      <Section title="Our Commitment">
        <p>
          Niggunim is committed to making the app accessible to as many people as possible,
          including those with disabilities. We believe that everyone should be able to enjoy music
          games regardless of their abilities.
        </p>
        <p>
          We are actively working to ensure our web app conforms to the{" "}
          <strong className="text-white">
            Web Content Accessibility Guidelines (WCAG) 2.1 Level AA
          </strong>
          , as published by the World Wide Web Consortium (W3C).
        </p>
      </Section>

      {/* What we've done */}
      <Section title="Accessibility Features">
        <ul className="list-disc list-inside space-y-2">
          <li>Semantic HTML elements and ARIA labels where appropriate</li>
          <li>Sufficient color contrast between text and background elements</li>
          <li>Interactive controls are keyboard-focusable</li>
          <li>Audio controls include visible play/stop buttons</li>
          <li>Text is resizable without loss of content or functionality</li>
          <li>Focus indicators are visible on interactive elements</li>
        </ul>
      </Section>

      {/* Known limitations */}
      <Section title="Known Limitations">
        <p>
          We are aware of some areas where accessibility could be improved:
        </p>
        <ul className="list-disc list-inside space-y-2 mt-2">
          <li>
            The core game mechanic involves listening to audio, which creates an inherent barrier for
            users with hearing impairments. We are exploring ways to provide alternative cues or
            accessibility modes in future updates.
          </li>
          <li>
            Some animated transitions may not fully respect the
            <code className="mx-1 px-1.5 py-0.5 bg-white/10 rounded text-xs font-mono">
              prefers-reduced-motion
            </code>
            setting in all browsers.
          </li>
          <li>
            Screen reader support for some interactive game elements is still being refined.
          </li>
        </ul>
      </Section>

      {/* Standards */}
      <Section title="Standards We Target">
        <div className="glass rounded-xl p-4 flex flex-col gap-2">
          <div className="flex items-start gap-3">
            <span className="text-purple-400 font-bold text-sm flex-shrink-0 mt-0.5">WCAG 2.1</span>
            <p className="text-zinc-300">
              Web Content Accessibility Guidelines 2.1, Level AA — our primary accessibility
              standard.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-purple-400 font-bold text-sm flex-shrink-0 mt-0.5">ARIA</span>
            <p className="text-zinc-300">
              WAI-ARIA 1.2 practices for rich internet application semantics and keyboard navigation.
            </p>
          </div>
        </div>
      </Section>

      {/* How to report */}
      <Section title="Report an Issue">
        <p>
          We welcome feedback on accessibility. If you experience any barriers while using
          Niggunim, please let us know so we can improve.
        </p>
        <p>
          When reporting an issue, it helps to include:
        </p>
        <ul className="list-disc list-inside space-y-1.5 mt-2">
          <li>A description of the accessibility barrier</li>
          <li>The browser and assistive technology you are using (e.g., screen reader name and version)</li>
          <li>The device type (desktop, mobile, tablet)</li>
          <li>Steps to reproduce the issue if possible</li>
        </ul>
        <p className="mt-2">
          We aim to respond to accessibility feedback within 5 business days.
        </p>
      </Section>

      {/* Contact */}
      <Section title="Contact">
        <p>
          To report an accessibility issue or ask for assistance:
        </p>
        <div className="mt-2 glass rounded-xl p-4">
          <p className="font-semibold text-white">Niggunim Accessibility</p>
          <p className="text-sm text-zinc-400 mt-0.5">
            Email:{" "}
            <a
              href="mailto:contact@niggunim.net"
              className="text-purple-400 underline hover:text-[#00E0FF]"
            >
              contact@niggunim.net
            </a>
          </p>
          <p className="text-sm text-zinc-400 mt-0.5">Website: www.niggunim.net</p>
        </div>
      </Section>
    </div>
  );
}

